import Airtable from 'airtable';
import {
  Company,
  Position,
  Candidate,
  PotentialCandidate,
  Application,
  Interview,
  TimelineEvent,
  User,
  UserSession,
  AuditLog,
  DailyReportSimple,
  DailyReportAdvanced,
  EmailTemplate,
  EmailConversation,
  ConversationMessage,
  PaginationParams,
  FilterParams,
  ApiResponse
} from '../types';

// Access control interface for dependency injection
interface AccessControlProvider {
  getUserCompanyId(): string | null;
  canAccessAllData(): boolean;
}

let accessControlProvider: AccessControlProvider | null = null;

class AirtableService {
  private base: any;

  constructor() {
    // Use the correct base ID for professional authentication
    const baseId = process.env.REACT_APP_AIRTABLE_BASE_ID || 'appRQUEimeQjVSXS2';
    const apiKey = process.env.REACT_APP_AIRTABLE_API_KEY;

    if (apiKey) {
      Airtable.configure({
        endpointUrl: 'https://api.airtable.com',
        apiKey: apiKey
      });
      this.base = Airtable.base(baseId);
      console.log(`Airtable configured with base: ${baseId}`);
    } else {
      console.warn('Airtable API key not configured');
    }
  }

  // Set access control provider for company-based filtering
  setAccessControlProvider(provider: AccessControlProvider) {
    accessControlProvider = provider;
  }

  // Apply company-based filtering to filter parameters
  private applyCompanyFilter(params: Partial<FilterParams & PaginationParams> | undefined): Partial<FilterParams & PaginationParams> {
    const effectiveParams: Partial<FilterParams & PaginationParams> = { ...params };

    // If access control is available and user should have company filtering applied
    if (accessControlProvider && !accessControlProvider.canAccessAllData()) {
      const userCompanyId = accessControlProvider.getUserCompanyId();
      if (userCompanyId) {
        effectiveParams.companyId = userCompanyId;
      }
    }

    return effectiveParams;
  }

  // Get company-scoped candidate filter for candidates that have applied to positions at the user's company
  private async getCompanyScopedCandidateFilter(): Promise<string | null> {
    if (!accessControlProvider || accessControlProvider.canAccessAllData()) {
      return null; // No filtering needed for administrators
    }

    const userCompanyId = accessControlProvider.getUserCompanyId();
    if (!userCompanyId) {
      return null;
    }

    try {
      // Get applications for positions at the user's company
      const applicationsResponse = await this.getApplications({ companyId: userCompanyId });
      if (!applicationsResponse.success || applicationsResponse.data.length === 0) {
        return 'RECORD_ID() = "impossible"'; // No candidates if no applications
      }

      // Extract unique candidate IDs
      const candidateIds = Array.from(new Set(
        applicationsResponse.data.map(app => app.candidateId).filter(id => id)
      ));

      if (candidateIds.length === 0) {
        return 'RECORD_ID() = "impossible"'; // No candidates
      }

      // Create OR filter for candidate IDs
      const candidateFilters = candidateIds.map(id => `RECORD_ID() = "${id}"`);
      return `OR(${candidateFilters.join(', ')})`;

    } catch (error) {
      console.error('Error creating company-scoped candidate filter:', error);
      return 'RECORD_ID() = "impossible"'; // Safe fallback
    }
  }

  private transformRecord(record: any, fields: any): any {
    return {
      id: record.id,
      ...fields,
      createdDate: new Date(record.createdTime)
    };
  }

  // Helper function to parse date strings as local dates instead of UTC
  private parseLocalDate(dateString: string): Date | null {
    if (!dateString) return null;

    // Handle date strings in YYYY-MM-DD format
    const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      // Create date in local timezone (month is 0-indexed)
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    // For other formats, fall back to default parsing
    return new Date(dateString);
  }

  // Companies
  async getCompanies(params?: FilterParams & PaginationParams): Promise<ApiResponse<Company[]>> {
    try {
      const selectOptions: any = {
        maxRecords: params?.limit || 100,
        sort: [{ field: 'Company Name', direction: 'asc' }]
      };

      if (params?.search) {
        selectOptions.filterByFormula = `SEARCH("${params.search}", {Company Name})`;
      }

      const records = await this.base('Companies').select(selectOptions).all();

      const companies: Company[] = records.map((record: any) =>
        this.transformRecord(record, {
          name: record.get('Company Name'),
          industry: record.get('Industry'),
          size: record.get('Company Size'),
          website: record.get('Website'),
          primaryContactName: record.get('Primary Contact Name'),
          primaryContactEmail: record.get('Primary Contact Email'),
          primaryContactPhone: record.get('Primary Contact Phone'),
          address: record.get('Address'),
          notes: record.get('Notes'),
          status: record.get('Status'),
          logo: record.get('Logo'),
          visibleInUI: record.get('Visible in UI') || false,
          companyPasscode: record.get('Company Passcode')
        })
      );

      return { data: companies, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  async createCompany(company: Omit<Company, 'id' | 'createdDate'>): Promise<ApiResponse<Company>> {
    try {
      const record = await this.base('Companies').create([
        {
          fields: {
            'Company Name': company.name,
            'Industry': company.industry,
            'Company Size': company.size,
            'Website': company.website,
            'Primary Contact Name': company.primaryContactName,
            'Primary Contact Email': company.primaryContactEmail,
            'Primary Contact Phone': company.primaryContactPhone,
            'Address': company.address,
            'Notes': company.notes,
            'Status': company.status
          }
        }
      ]);

      const createdCompany = this.transformRecord(record[0], {
        name: record[0].get('Company Name'),
        industry: record[0].get('Industry'),
        size: record[0].get('Company Size'),
        website: record[0].get('Website'),
        primaryContactName: record[0].get('Primary Contact Name'),
        primaryContactEmail: record[0].get('Primary Contact Email'),
        primaryContactPhone: record[0].get('Primary Contact Phone'),
        address: record[0].get('Address'),
        notes: record[0].get('Notes'),
        status: record[0].get('Status')
      });

      return { data: createdCompany, success: true };
    } catch (error) {
      return { data: {} as Company, success: false, error: (error as Error).message };
    }
  }

  // Get companies that are visible in login UI
  async getVisibleCompanies(): Promise<ApiResponse<Company[]>> {
    try {
      if (!this.base) {
        return { data: [], success: false, error: 'Airtable not configured' };
      }

      const records = await this.base('Companies').select({
        filterByFormula: 'AND({Visible in UI} = TRUE(), {Status} = "Active")',
        sort: [{ field: 'Company Name', direction: 'asc' }]
      }).all();

      const companies: Company[] = records.map((record: any) =>
        this.transformRecord(record, {
          name: record.get('Company Name'),
          industry: record.get('Industry'),
          size: record.get('Company Size'),
          website: record.get('Website'),
          primaryContactName: record.get('Primary Contact Name'),
          primaryContactEmail: record.get('Primary Contact Email'),
          primaryContactPhone: record.get('Primary Contact Phone'),
          address: record.get('Address'),
          notes: record.get('Notes'),
          status: record.get('Status'),
          logo: record.get('Logo'),
          visibleInUI: record.get('Visible in UI') || false,
          companyPasscode: record.get('Company Passcode')
        })
      );

      return { data: companies, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  // Verify company passcode
  async verifyCompanyPasscode(companyId: string, passcode: string): Promise<ApiResponse<boolean>> {
    try {
      if (!this.base) {
        return { data: false, success: false, error: 'Airtable not configured' };
      }

      const record = await this.base('Companies').find(companyId);
      const storedPasscode = record.get('Company Passcode');

      if (!storedPasscode) {
        return { data: false, success: false, error: 'Company passcode not configured' };
      }

      const isValid = storedPasscode === passcode;
      return { data: isValid, success: true };
    } catch (error) {
      return { data: false, success: false, error: (error as Error).message };
    }
  }

  async getCompanyById(id: string): Promise<ApiResponse<Company>> {
    try {
      if (!this.base) {
        return { data: {} as Company, success: false, error: 'Airtable not configured' };
      }

      const record = await this.base('Companies').find(id);

      const company: Company = {
        id: record.id,
        name: record.get('Company Name') as string,
        industry: record.get('Industry') as string,
        size: record.get('Size') as string,
        website: record.get('Website') as string,
        primaryContactName: record.get('Primary Contact Name') as string,
        primaryContactEmail: record.get('Primary Contact Email') as string,
        primaryContactPhone: record.get('Primary Contact Phone') as string,
        address: record.get('Address') as string,
        notes: record.get('Notes') as string,
        status: record.get('Status') as 'Active' | 'Inactive' | 'On Hold',
        logo: record.get('Logo') as any,
        createdDate: new Date(record.get('Created Date') as string || record.createdTime),
        visibleInUI: record.get('Visible in UI') as boolean || false,
        companyPasscode: record.get('Company Passcode') as string
      };

      return {
        success: true,
        data: company
      };
    } catch (error) {
      console.error('Error fetching company by ID:', error);
      return {
        success: false,
        error: 'Failed to fetch company',
        data: {} as Company
      };
    }
  }

  // Positions
  async getPositions(params?: Partial<FilterParams & PaginationParams>): Promise<ApiResponse<Position[]>> {
    try {
      // Apply company-based access control filtering
      const effectiveParams = this.applyCompanyFilter(params) || {};

      let filterFormula = '';
      if (effectiveParams?.search) {
        filterFormula = `SEARCH("${effectiveParams.search}", {Job Title})`;
      }
      if (effectiveParams?.companyId) {
        const companyFilter = `{Company} = "${effectiveParams.companyId}"`;
        filterFormula = filterFormula ? `AND(${filterFormula}, ${companyFilter})` : companyFilter;
      }

      const selectOptions: any = {
        maxRecords: params?.limit || 100,
        sort: [{ field: 'Job Title', direction: 'asc' }]
      };

      if (filterFormula) {
        selectOptions.filterByFormula = filterFormula;
      }

      const records = await this.base('Positions').select(selectOptions).all();

      const positions: Position[] = records.map((record: any) =>
        this.transformRecord(record, {
          companyId: record.get('Company')?.[0],
          title: record.get('Job Title'),
          department: record.get('Department'),
          employmentType: record.get('Employment Type'),
          location: record.get('Location'),
          remoteWork: record.get('Remote Work'),
          salaryRangeMin: record.get('Salary Range Min'),
          salaryRangeMax: record.get('Salary Range Max'),
          currency: record.get('Currency'),
          description: record.get('Job Description'),
          requirements: record.get('Requirements'),
          benefits: record.get('Benefits'),
          hiringManager: record.get('Hiring Manager'),
          hiringManagerEmail: record.get('Hiring Manager Email'),
          priority: record.get('Priority Level'),
          status: record.get('Status'),
          targetStartDate: record.get('Target Start Date') ? new Date(record.get('Target Start Date')) : undefined,
          deadline: record.get('Deadline') ? new Date(record.get('Deadline')) : undefined
        })
      );

      return { data: positions, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  // Candidates
  async getCandidates(params?: Partial<FilterParams & PaginationParams>): Promise<ApiResponse<Candidate[]>> {
    try {
      let filterFormula = '';

      // Apply company-based access control for candidates
      const companyFilter = await this.getCompanyScopedCandidateFilter();
      if (companyFilter) {
        filterFormula = companyFilter;
      }

      if (params?.search) {
        const searchFilter = `OR(SEARCH("${params.search}", {First Name}), SEARCH("${params.search}", {Last Name}), SEARCH("${params.search}", {Email}))`;
        filterFormula = filterFormula ? `AND(${filterFormula}, ${searchFilter})` : searchFilter;
      }
      if (params?.status) {
        const statusFilter = `{Status} = "${params.status}"`;
        filterFormula = filterFormula ? `AND(${filterFormula}, ${statusFilter})` : statusFilter;
      }

      const selectOptions: any = {
        maxRecords: params?.limit || 100,
        sort: [{ field: 'Last Name', direction: 'asc' }]
      };

      if (filterFormula) {
        selectOptions.filterByFormula = filterFormula;
      }

      const records = await this.base('Candidates').select(selectOptions).all();

      console.log('=== AIRTABLE SERVICE - getCandidates Debug ===');
      console.log('Total records fetched from Airtable:', records.length);

      const candidates: Candidate[] = records.map((record: any) => {
        const showInUIValue = record.get('Show in UI');
        console.log(`Record: ${record.get('First Name')} ${record.get('Last Name')}, Show in UI raw:`, showInUIValue, `Type: ${typeof showInUIValue}`);

        return this.transformRecord(record, {
          firstName: record.get('First Name'),
          lastName: record.get('Last Name'),
          email: record.get('Email'),
          phone: record.get('Phone Number') || record.get('Phone'),
          currentLocation: record.get('Current Location'),
          willingToRelocate: record.get('Willing to Relocate'),
          currentJobTitle: record.get('Current Job Title'),
          currentCompany: record.get('Current Company'),
          yearsOfExperience: record.get('Years of Experience'),
          linkedinProfile: record.get('LinkedIn Profile'),
          resumeUrl: record.get('Resume URL'),
          portfolioUrl: record.get('Portfolio URL'),
          resumeAttachment: record.get('Resume Attachment'),
          candidateImage: record.get('Candidate Image'),
          salaryExpectation: record.get('Salary Expectation'),
          noticePeriod: record.get('Notice Period'),
          source: record.get('Source'),
          notes: record.get('Notes'),
          status: record.get('Status'),
          dateActivated: record.get('Date Candidate Activated') ? new Date(record.get('Date Candidate Activated')) : null,
          accessPasscode: record.get('Access Passcode'),
          accessCode: record.get('Access Code'),
          showInUI: showInUIValue
        });
      }).filter((candidate: Candidate) => {
        const shouldShow = candidate.showInUI === true;
        console.log(`Filter: ${candidate.firstName} ${candidate.lastName}, showInUI: ${candidate.showInUI}, Will show: ${shouldShow}`);
        return shouldShow;
      });

      console.log('Total candidates after filter:', candidates.length);

      return { data: candidates, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  async createCandidate(candidate: Omit<Candidate, 'id' | 'createdDate'>): Promise<ApiResponse<Candidate>> {
    try {
      const record = await this.base('Candidates').create([
        {
          fields: {
            'First Name': candidate.firstName,
            'Last Name': candidate.lastName,
            'Email': candidate.email,
            'Phone': candidate.phone,
            'Current Location': candidate.currentLocation,
            'Willing to Relocate': candidate.willingToRelocate,
            'Current Job Title': candidate.currentJobTitle,
            'Current Company': candidate.currentCompany,
            'Years of Experience': candidate.yearsOfExperience,
            'LinkedIn Profile': candidate.linkedinProfile,
            'Resume URL': candidate.resumeUrl,
            'Portfolio URL': candidate.portfolioUrl,
            'Salary Expectation': candidate.salaryExpectation,
            'Notice Period': candidate.noticePeriod,
            'Source': candidate.source,
            'Notes': candidate.notes,
            'Status': candidate.status
          }
        }
      ]);

      const createdCandidate = this.transformRecord(record[0], {
        firstName: record[0].get('First Name'),
        lastName: record[0].get('Last Name'),
        email: record[0].get('Email'),
        phone: record[0].get('Phone'),
        currentLocation: record[0].get('Current Location'),
        willingToRelocate: record[0].get('Willing to Relocate'),
        currentJobTitle: record[0].get('Current Job Title'),
        currentCompany: record[0].get('Current Company'),
        yearsOfExperience: record[0].get('Years of Experience'),
        linkedinProfile: record[0].get('LinkedIn Profile'),
        resumeUrl: record[0].get('Resume URL'),
        portfolioUrl: record[0].get('Portfolio URL'),
        resumeAttachment: record[0].get('Resume Attachment'),
        candidateImage: record[0].get('Candidate Image'),
        salaryExpectation: record[0].get('Salary Expectation'),
        noticePeriod: record[0].get('Notice Period'),
        source: record[0].get('Source'),
        notes: record[0].get('Notes'),
        status: record[0].get('Status')
      });

      return { data: createdCandidate, success: true };
    } catch (error) {
      return { data: {} as Candidate, success: false, error: (error as Error).message };
    }
  }

  // Potential Candidates (Identified-Possible-Candidates table)
  async getPotentialCandidates(params?: Partial<FilterParams & PaginationParams>): Promise<ApiResponse<PotentialCandidate[]>> {
    try {
      let filterFormula = '';

      if (params?.search) {
        const searchFilter = `OR(SEARCH("${params.search}", {candidate-name}), SEARCH("${params.search}", {email}), SEARCH("${params.search}", {company}))`;
        filterFormula = searchFilter;
      }
      if (params?.status) {
        const statusFilter = `{current-status} = "${params.status}"`;
        filterFormula = filterFormula ? `AND(${filterFormula}, ${statusFilter})` : statusFilter;
      }

      const selectOptions: any = {
        maxRecords: params?.limit || 100,
        sort: [{ field: 'candidate-name', direction: 'asc' }]
      };

      if (filterFormula) {
        selectOptions.filterByFormula = filterFormula;
      }

      const records = await this.base('Identified-Possible-Candidates').select(selectOptions).all();

      console.log('=== AIRTABLE SERVICE - getPotentialCandidates Debug ===');
      console.log('Total records fetched from Airtable:', records.length);

      const potentialCandidates: PotentialCandidate[] = records.map((record: any) => {
        return this.transformRecord(record, {
          index: record.get('index'),
          openingStatus: record.get('opening-status'),
          candidateName: record.get('candidate-name'),
          linkedinProfile: record.get('linkedin-profile'),
          generalNotes: record.get('general-notes'),
          company: record.get('company'),
          title: record.get('title'),
          location: record.get('location'),
          currentStatus: record.get('current-status'),
          telNumber: record.get('tel-number'),
          email: record.get('email'),
          notes: record.get('notes'),
          level: record.get('level'),
          rraNotes: record.get('rra-notes'),
          nextStepsContactEmail: record.get('next-steps-contact-email'),
          okToContact: record.get('ok-to-contact'),
          candidateImage: record.get('candidate-image')
        });
      });

      console.log('Total potential candidates after processing:', potentialCandidates.length);

      return { data: potentialCandidates, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  async getPotentialCandidateById(id: string): Promise<ApiResponse<PotentialCandidate>> {
    try {
      if (!this.base) {
        return { data: {} as PotentialCandidate, success: false, error: 'Airtable not configured' };
      }

      const record = await this.base('Identified-Possible-Candidates').find(id);

      const potentialCandidate: PotentialCandidate = this.transformRecord(record, {
        index: record.get('index'),
        openingStatus: record.get('opening-status'),
        candidateName: record.get('candidate-name'),
        linkedinProfile: record.get('linkedin-profile'),
        generalNotes: record.get('general-notes'),
        company: record.get('company'),
        title: record.get('title'),
        location: record.get('location'),
        currentStatus: record.get('current-status'),
        telNumber: record.get('tel-number'),
        email: record.get('email'),
        notes: record.get('notes'),
        level: record.get('level'),
        rraNotes: record.get('rra-notes'),
        nextStepsContactEmail: record.get('next-steps-contact-email'),
        okToContact: record.get('ok-to-contact'),
        candidateImage: record.get('candidate-image')
      });

      return { success: true, data: potentialCandidate };
    } catch (error) {
      console.error('Error fetching potential candidate by ID:', error);
      return { success: false, error: 'Failed to fetch potential candidate', data: {} as PotentialCandidate };
    }
  }

  // Applications
  async getApplications(params?: Partial<FilterParams & PaginationParams>): Promise<ApiResponse<Application[]>> {
    try {
      // Apply company-based access control filtering
      const effectiveParams = this.applyCompanyFilter(params) || {};

      let filterFormula = '';
      if (effectiveParams?.positionId) {
        filterFormula = `{Position} = "${effectiveParams.positionId}"`;
      }
      if (effectiveParams?.candidateId) {
        const candidateFilter = `{Candidate} = "${effectiveParams.candidateId}"`;
        filterFormula = filterFormula ? `AND(${filterFormula}, ${candidateFilter})` : candidateFilter;
      }

      // Company-based filtering through positions
      if (effectiveParams?.companyId && !effectiveParams?.positionId) {
        // Get positions for the user's company to filter applications
        const positionsResponse = await this.getPositions({ companyId: effectiveParams.companyId });
        if (positionsResponse.success && positionsResponse.data.length > 0) {
          const positionIds = positionsResponse.data.map(pos => pos.id);
          const positionFilters = positionIds.map(id => `{Position} = "${id}"`);
          const companyPositionsFilter = `OR(${positionFilters.join(', ')})`;
          filterFormula = filterFormula ? `AND(${filterFormula}, ${companyPositionsFilter})` : companyPositionsFilter;
        } else {
          // No positions for this company, return no applications
          return { data: [], success: true };
        }
      }

      const selectOptions: any = {
        maxRecords: params?.limit || 100,
        sort: [{ field: 'Application Date', direction: 'desc' }]
      };

      if (filterFormula) {
        selectOptions.filterByFormula = filterFormula;
      }

      const records = await this.base('Applications').select(selectOptions).all();

      const applications: Application[] = records.map((record: any) =>
        this.transformRecord(record, {
          candidateId: record.get('Candidate')?.[0],
          positionId: record.get('Position')?.[0],
          applicationDate: new Date(record.get('Application Date')),
          applicationStatus: record.get('Application Status'),
          currentStage: record.get('Current Stage'),
          priority: record.get('Priority'),
          m4Consultant: record.get('M4 Consultant'),
          notes: record.get('Notes'),
          rejectionReason: record.get('Rejection Reason'),
          lastUpdated: new Date(record.get('Last Updated'))
        })
      );

      return { data: applications, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  // Get Interviewers
  async getInterviewers(): Promise<ApiResponse<any[]>> {
    try {
      const records = await this.base('Interviewers').select({
        maxRecords: 200,
        sort: [{ field: 'First Name', direction: 'asc' }]
      }).all();

      const interviewers = records.map((record: any) => ({
        id: record.id,
        firstName: record.get('First Name'),
        lastName: record.get('Last Name'),
        email: record.get('Email'),
        jobTitle: record.get('Job Title'),
        department: record.get('Department'),
        phone: record.get('Phone'),
        role: record.get('Role in Interview'),
        timezone: record.get('Timezone'),
        preferredInterviewTimes: record.get('Preferred Interview Times'),
        active: record.get('Active Status'),
        bio: record.get('Bio'),
        notes: record.get('Notes'),
        createdDate: new Date(record.createdTime)
      }));

      return { data: interviewers, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  // Interviews
  async getInterviews(params?: FilterParams & PaginationParams): Promise<ApiResponse<Interview[]>> {
    try {
      let filterFormula = '';
      if (params?.status) {
        filterFormula = `{Interview Status} = "${params.status}"`;
      }
      if (params?.search) {
        const searchFilter = `OR(SEARCH("${params.search}", {Interview Type}), SEARCH("${params.search}", {Interview Stage}))`;
        filterFormula = filterFormula ? `AND(${filterFormula}, ${searchFilter})` : searchFilter;
      }

      const selectOptions: any = {
        maxRecords: params?.limit || 100,
        sort: [{ field: 'Scheduled Date', direction: 'desc' }]
      };

      if (filterFormula) {
        selectOptions.filterByFormula = filterFormula;
      }

      // First get all interviewers to cross-reference names
      const interviewersResponse = await this.getInterviewers();
      const interviewersMap = new Map();
      if (interviewersResponse.success) {
        interviewersResponse.data.forEach(interviewer => {
          interviewersMap.set(interviewer.id, `${interviewer.firstName} ${interviewer.lastName}`);
        });
      }

      const records = await this.base('Interviews').select(selectOptions).all();

      console.log('🔍 Raw Interviews record debug:', {
        total: records.length,
        sampleRecord: records[0] ? {
          id: records[0].id,
          allFields: records[0].fields,
          interviewType: records[0].get('Interview Type'),
          interviewStage: records[0].get('Interview Stage'),
          interviewStatus: records[0].get('Interview Status'),
          scheduledDate: records[0].get('Scheduled Date'),
          scheduledTime: records[0].get('Scheduled Time'),
          duration: records[0].get('Duration'),
          interviewersField: records[0].get('Interviewers'),
          candidateImageField: records[0].get('Candidate Image'),
          firstNameFromInterviewers: records[0].get('First Name (from Interviewers)'),
          lastNameFromInterviewers: records[0].get('Last Name (from Interviewers)')
        } : 'No records'
      });

      console.log('🔍 Interviewers map:', Object.fromEntries(interviewersMap));

      const interviews: Interview[] = records.map((record: any) => {
        // Get interviewer IDs from the Interviewers field (array of linked records)
        const interviewerIds = record.get('Interviewers') || [];
        let interviewerName = '';

        // Get the first interviewer's name from our map
        if (interviewerIds.length > 0) {
          const interviewerId = interviewerIds[0];
          interviewerName = interviewersMap.get(interviewerId) || '';
        }

        // Try different field variations for candidate image
        let candidateImage = record.get('Candidate Image') ||
                           record.get('Candidate Image (from Application)') ||
                           record.get('Candidate Image (from Candidate)');

        console.log('🎯 Interview record mapping:', {
          id: record.id,
          interviewType: record.get('Interview Type'),
          interviewStage: record.get('Interview Stage'),
          interviewStatus: record.get('Interview Status'),
          scheduledDate: record.get('Scheduled Date'),
          scheduledTime: record.get('Scheduled Time'),
          duration: record.get('Duration (minutes)'),
          location: record.get('Location'),
          meetingLink: record.get('Meeting Link'),
          interviewNotes: record.get('Interview Notes'),
          interviewerIds: record.get('Interviewers'),
          interviewerName: interviewerName,
          candidateImage: candidateImage ? 'Image found' : 'No image',
          candidateImageUrl: candidateImage?.[0]?.url,
          createdTime: record.createdTime
        });

        return this.transformRecord(record, {
          applicationId: record.get('Application')?.[0],
          type: record.get('Interview Type'),
          stage: record.get('Interview Stage'),
          interviewers: record.get('Interviewers') || [],
          interviewerName: interviewerName,
          scheduledDate: record.get('Scheduled Date') ? this.parseLocalDate(record.get('Scheduled Date')) : undefined,
          scheduledTime: record.get('Scheduled Time'),
          duration: record.get('Duration (minutes)'),
          meetingLink: record.get('Meeting Link'),
          location: record.get('Location'),
          previousInterviewer: record.get('Previous Interviewer'),
          status: record.get('Interview Status'),
          rescheduleReason: record.get('Reschedule Reason'),
          interviewNotes: record.get('Interview Notes'),
          technicalAssessmentUrl: record.get('Technical Assessment URL'),
          candidateImage: candidateImage,
          candidateFirstName: (record.get('First Name') as string[])?.[0], // First Name lookup field
          candidateLastName: (record.get('Last Name') as string[])?.[0], // Last Name lookup field
          createdDate: new Date(record.createdTime)
        });
      });

      return { data: interviews, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  // Get Single Interview by ID
  async getInterviewById(interviewId: string): Promise<ApiResponse<Interview>> {
    try {
      if (!this.base) {
        return { data: {} as Interview, success: false, error: 'Airtable not configured' };
      }

      // First get all interviewers to cross-reference names
      const interviewersResponse = await this.getInterviewers();
      const interviewersMap = new Map();
      if (interviewersResponse.success) {
        interviewersResponse.data.forEach(interviewer => {
          interviewersMap.set(interviewer.id, `${interviewer.firstName} ${interviewer.lastName}`);
        });
      }

      // Get the specific interview record
      const record = await this.base('Interviews').find(interviewId);

      // Get interviewer IDs from the Interviewers field (array of linked records)
      const interviewerIds = record.get('Interviewers') || [];
      let interviewerName = '';

      // Get the first interviewer's name from our map
      if (interviewerIds.length > 0) {
        const firstInterviewerId = interviewerIds[0];
        interviewerName = interviewersMap.get(firstInterviewerId) || '';
      }

      // Try different field variations for candidate image
      let candidateImage = record.get('Candidate Image') ||
                         record.get('Candidate Image (from Application)') ||
                         record.get('Candidate Image (from Candidate)');

      const interview = this.transformRecord(record, {
        applicationId: record.get('Application')?.[0],
        type: record.get('Interview Type'),
        stage: record.get('Interview Stage'),
        interviewers: record.get('Interviewers') || [],
        interviewerName: interviewerName,
        scheduledDate: record.get('Scheduled Date') ? this.parseLocalDate(record.get('Scheduled Date')) : undefined,
        scheduledTime: record.get('Scheduled Time'),
        duration: record.get('Duration (minutes)'),
        meetingLink: record.get('Meeting Link'),
        location: record.get('Location'),
        status: record.get('Interview Status'),
        rescheduleReason: record.get('Reschedule Reason'),
        interviewNotes: record.get('Interview Notes'),
        technicalAssessmentUrl: record.get('Technical Assessment URL'),
        candidateImage: candidateImage,
        candidateFirstName: (record.get('First Name') as string[])?.[0], // First Name lookup field
        candidateLastName: (record.get('Last Name') as string[])?.[0], // Last Name lookup field
        createdDate: new Date(record.createdTime)
      });

      return { data: interview, success: true };
    } catch (error) {
      return { data: {} as Interview, success: false, error: (error as Error).message };
    }
  }

  // Timeline Events
  async getTimelineEvents(params?: FilterParams & PaginationParams): Promise<ApiResponse<TimelineEvent[]>> {
    try {
      if (!this.base) {
        return { data: [], success: false, error: 'Airtable not configured' };
      }

      const selectOptions: any = {
        maxRecords: params?.limit || 100,
        sort: [{ field: 'Event Date', direction: 'desc' }]
      };

      if (params?.search) {
        selectOptions.filterByFormula = `OR(SEARCH("${params.search}", {Description}), SEARCH("${params.search}", {Notes}))`;
      }

      if (params?.dateFrom && params?.dateTo) {
        const dateFilter = `AND(IS_AFTER({Event Date}, '${params.dateFrom.toISOString().split('T')[0]}'), IS_BEFORE({Event Date}, '${params.dateTo.toISOString().split('T')[0]}'))`;
        selectOptions.filterByFormula = selectOptions.filterByFormula
          ? `AND(${selectOptions.filterByFormula}, ${dateFilter})`
          : dateFilter;
      }

      const records = await this.base('Timeline Events').select(selectOptions).all();

      const events: TimelineEvent[] = records.map((record: any) =>
        this.transformRecord(record, {
          applicationId: record.get('Application')?.[0],
          eventType: record.get('Event Type'),
          eventDate: new Date(record.get('Event Date')),
          eventTime: record.get('Event Time'),
          previousStatus: record.get('Previous Status'),
          newStatus: record.get('New Status'),
          description: record.get('Description'),
          createdBy: record.get('Created By'),
          automatedEvent: record.get('Automated Event'),
          relatedInterviewId: record.get('Related Interview')?.[0],
          notes: record.get('Notes'),
          currentStep: record.get('Current Step'),
          candidateImage: record.get('Candidate Image'),
          nextSteps: record.get('Next Steps'),
          availableMeetTimes: record.get('Available Meet Times'),
          candidateQuestions: record.get('Questions'), // Lookup field from Questions and Answers table
          adminAnswers: record.get('Answers'), // Lookup field from Questions and Answers table
          typicalCandidateQuestions: record.get('Typical Candidate Questions'), // Pre-set typical questions
          typicalAdminAnswers: record.get('Admin Answers') // Pre-set admin answers for typical questions
        })
      );

      return { data: events, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  async createTimelineEvent(event: Omit<TimelineEvent, 'id'>): Promise<ApiResponse<TimelineEvent>> {
    try {
      const record = await this.base('Timeline Events').create([
        {
          fields: {
            'Application': [event.applicationId],
            'Event Type': event.eventType,
            'Event Date': event.eventDate.toISOString().split('T')[0],
            'Event Time': event.eventTime,
            'Previous Status': event.previousStatus,
            'New Status': event.newStatus,
            'Description': event.description,
            'Created By': event.createdBy,
            'Automated Event': event.automatedEvent,
            'Related Interview': event.relatedInterviewId ? [event.relatedInterviewId] : undefined,
            'Notes': event.notes,
            'Current Step': event.currentStep,
            'Next Steps': event.nextSteps,
            'Available Meet Times': event.availableMeetTimes
          }
        }
      ]);

      const createdEvent = this.transformRecord(record[0], {
        applicationId: record[0].get('Application')?.[0],
        eventType: record[0].get('Event Type'),
        eventDate: new Date(record[0].get('Event Date')),
        eventTime: record[0].get('Event Time'),
        previousStatus: record[0].get('Previous Status'),
        newStatus: record[0].get('New Status'),
        description: record[0].get('Description'),
        createdBy: record[0].get('Created By'),
        automatedEvent: record[0].get('Automated Event'),
        relatedInterviewId: record[0].get('Related Interview')?.[0],
        notes: record[0].get('Notes'),
        currentStep: record[0].get('Current Step'),
        nextSteps: record[0].get('Next Steps'),
        availableMeetTimes: record[0].get('Available Meet Times'),
        candidateQuestions: record[0].get('Questions'), // Lookup field from Questions and Answers table
        adminAnswers: record[0].get('Answers'), // Lookup field from Questions and Answers table
        typicalCandidateQuestions: record[0].get('Typical Candidate Questions'), // Pre-set typical questions
        typicalAdminAnswers: record[0].get('Admin Answers') // Pre-set admin answers for typical questions
      });

      return { data: createdEvent, success: true };
    } catch (error) {
      return { data: {} as TimelineEvent, success: false, error: (error as Error).message };
    }
  }

  // Update methods
  async updateApplication(id: string, updates: Partial<Application>): Promise<ApiResponse<Application>> {
    try {
      const fields: any = {};
      if (updates.applicationStatus) fields['Application Status'] = updates.applicationStatus;
      if (updates.currentStage) fields['Current Stage'] = updates.currentStage;
      if (updates.priority) fields['Priority'] = updates.priority;
      if (updates.notes) fields['Notes'] = updates.notes;
      if (updates.rejectionReason) fields['Rejection Reason'] = updates.rejectionReason;

      const record = await this.base('Applications').update([
        {
          id: id,
          fields: fields
        }
      ]);

      const updatedApplication = this.transformRecord(record[0], {
        candidateId: record[0].get('Candidate')?.[0],
        positionId: record[0].get('Position')?.[0],
        applicationDate: new Date(record[0].get('Application Date')),
        applicationStatus: record[0].get('Application Status'),
        currentStage: record[0].get('Current Stage'),
        priority: record[0].get('Priority'),
        m4Consultant: record[0].get('M4 Consultant'),
        notes: record[0].get('Notes'),
        rejectionReason: record[0].get('Rejection Reason'),
        lastUpdated: new Date(record[0].get('Last Updated'))
      });

      return { data: updatedApplication, success: true };
    } catch (error) {
      return { data: {} as Application, success: false, error: (error as Error).message };
    }
  }

  // User Authentication Methods
  async authenticateUser(email: string, password: string): Promise<ApiResponse<User>> {
    try {
      console.log('🔍 Authenticating user:', email);
      console.log('🔑 Using base ID:', process.env.REACT_APP_AIRTABLE_BASE_ID);
      console.log('🗝️ API key configured:', process.env.REACT_APP_AIRTABLE_API_KEY ? 'Yes' : 'No');

      if (!this.base) {
        console.error('❌ Airtable base not configured');
        return { data: {} as User, success: false, error: 'Airtable not configured' };
      }

      console.log('🔎 Searching for user in Users table...');
      // First, find the user by email
      const records = await this.base('Users').select({
        filterByFormula: `{Email} = "${email}"`,
        maxRecords: 1
      }).all();

      console.log('📊 Search results:', records.length, 'user(s) found');

      if (records.length === 0) {
        console.log('❌ No user found with email:', email);
        return { data: {} as User, success: false, error: 'Invalid email or password' };
      }

      const record = records[0];
      const storedPassword = record.get('Admin Passcode');
      const isActive = record.get('Active Status');
      // Note: Account locking fields don't exist in current Users table
      // If needed for production, add these fields to Airtable:
      // - Account Locked (Checkbox)
      // - Lock Expires (Date & time)
      // - Failed Login Attempts (Number)
      const isLocked = false; // Always false since field doesn't exist
      const lockExpires = null;

      console.log('👤 User found:', {
        email: record.get('Email'),
        firstName: record.get('First Name'),
        lastName: record.get('Last Name'),
        userType: record.get('User Type'),
        active: isActive,
        locked: isLocked,
        hasPassword: !!storedPassword
      });

      console.log('🔐 Password comparison:', {
        provided: password,
        stored: storedPassword,
        match: storedPassword === password
      });

      // Check if user is active — only block when explicitly set to false
      if (isActive === false) {
        console.log('❌ Login failed: Account is deactivated');
        return { data: {} as User, success: false, error: 'Account is deactivated' };
      }

      // Check if account is locked and lock hasn't expired
      if (isLocked && lockExpires && lockExpires > new Date()) {
        // Audit logging disabled - table may not exist
        // await this.createAuditLog({
        //   userId: record.id,
        //   action: 'Login Failed',
        //   description: 'Login attempt on locked account',
        //   result: 'Blocked'
        // });
        console.log('❌ Login failed: Account is locked');
        return { data: {} as User, success: false, error: 'Account is locked. Please try again later.' };
      }

      // If lock has expired, unlock the account
      if (isLocked && lockExpires && lockExpires <= new Date()) {
        await this.base('Users').update([
          {
            id: record.id,
            fields: {
              'Account Locked': false,
              'Failed Login Attempts': 0,
              'Lock Expires': null
            }
          }
        ]);
      }

      // For demo purposes, we'll do simple password comparison
      // In production, you should use proper password hashing (bcrypt, etc.)
      if (storedPassword !== password) {
        await this.updateUserLoginAttempts(record.id, true);
        // await this.createAuditLog({
        //   userId: record.id,
        //   action: 'Login Failed',
        //   description: 'Invalid password attempt',
        //   result: 'Failed'
        // });
        return { data: {} as User, success: false, error: 'Invalid email or password' };
      }

      // Note: Login tracking fields don't exist in current Users table
      // Skipping updateUserLoginAttempts call since fields don't exist
      console.log('✅ Login successful - user authenticated');

      // Log successful login - disabled since Audit Log table may not exist
      // await this.createAuditLog({
      //   userId: record.id,
      //   action: 'Login Success',
      //   description: `Successful login for ${email}`,
      //   result: 'Success'
      // });
      console.log('✅ Audit log skipped - table may not exist');

      const user = this.transformRecord(record, {
        email: record.get('Email'),
        firstName: record.get('First Name'),
        lastName: record.get('Last Name'),
        userType: record.get('User Type'),
        userGroupType: record.get('User Group Type'),
        companyId: record.get('Company')?.[0],
        candidateId: record.get('Candidate')?.[0],
        active: record.get('Active Status'),
        lastLogin: undefined, // Field doesn't exist in current table
        emailVerified: record.get('Email Verified') || false,
        // Note: The following fields don't exist in current Users table:
        // If needed, add them to Airtable Users table
        loginCount: 0,
        failedLoginAttempts: 0,
        accountLocked: false,
        lockExpires: undefined,
        passwordResetToken: undefined,
        passwordResetExpires: undefined,
        phone: record.get('Phone Number') || record.get('Phone'),
        department: undefined,
        jobTitle: undefined,
        managerId: undefined,
        permissions: [],
        timezone: undefined,
        language: undefined,
        profilePicture: undefined,
        notes: undefined
      });

      return { data: user, success: true };
    } catch (error) {
      return { data: {} as User, success: false, error: (error as Error).message };
    }
  }

  async getUserById(id: string): Promise<ApiResponse<User>> {
    try {
      if (!this.base) {
        return { data: {} as User, success: false, error: 'Airtable not configured' };
      }

      const record = await this.base('Users').find(id);

      const user = this.transformRecord(record, {
        email: record.get('Email'),
        firstName: record.get('First Name'),
        lastName: record.get('Last Name'),
        userType: record.get('User Type'),
        userGroupType: record.get('User Group Type'),
        companyId: record.get('Company')?.[0],
        candidateId: record.get('Candidate')?.[0],
        active: record.get('Active Status'),
        lastLogin: record.get('Last Login') ? new Date(record.get('Last Login')) : undefined,
        emailVerified: record.get('Email Verified') || false,
        loginCount: record.get('Login Count') || 0,
        failedLoginAttempts: record.get('Failed Login Attempts') || 0,
        accountLocked: record.get('Account Locked') || false,
        lockExpires: record.get('Lock Expires') ? new Date(record.get('Lock Expires')) : undefined,
        passwordResetToken: record.get('Password Reset Token'),
        passwordResetExpires: record.get('Password Reset Expires') ? new Date(record.get('Password Reset Expires')) : undefined,
        phone: record.get('Phone Number') || record.get('Phone'),
        department: record.get('Department'),
        jobTitle: record.get('Job Title'),
        managerId: record.get('Manager')?.[0],
        permissions: record.get('Permissions') || [],
        timezone: record.get('Timezone'),
        language: record.get('Language'),
        profilePicture: record.get('Profile Picture'),
        notes: record.get('Notes')
      });

      return { data: user, success: true };
    } catch (error) {
      return { data: {} as User, success: false, error: (error as Error).message };
    }
  }

  async getUsers(params?: FilterParams & PaginationParams): Promise<ApiResponse<User[]>> {
    try {
      if (!this.base) {
        return { data: [], success: false, error: 'Airtable not configured' };
      }

      const selectOptions: any = {
        maxRecords: params?.limit || 100,
        sort: [{ field: 'Last Name', direction: 'asc' }]
      };

      if (params?.search) {
        selectOptions.filterByFormula = `OR(SEARCH("${params.search}", {First Name}), SEARCH("${params.search}", {Last Name}), SEARCH("${params.search}", {Email}))`;
      }

      const records = await this.base('Users').select(selectOptions).all();

      const users: User[] = records.map((record: any) =>
        this.transformRecord(record, {
          email: record.get('Email'),
          firstName: record.get('First Name'),
          lastName: record.get('Last Name'),
          userType: record.get('User Type'),
          userGroupType: record.get('User Group Type'),
          companyId: record.get('Company')?.[0],
          candidateId: record.get('Candidate')?.[0],
          active: record.get('Active Status'),
          lastLogin: undefined,
          emailVerified: record.get('Email Verified') || false,
          loginCount: 0,
          failedLoginAttempts: 0,
          accountLocked: false,
          lockExpires: undefined,
          passwordResetToken: undefined,
          passwordResetExpires: undefined,
          phone: record.get('Phone Number') || record.get('Phone'),
          department: undefined,
          jobTitle: undefined,
          managerId: undefined,
          permissions: [],
          timezone: undefined,
          language: undefined,
          profilePicture: undefined,
          notes: undefined
        })
      );

      return { data: users, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  // Daily Reports - Simple
  async getDailyReportsSimple(params?: FilterParams & PaginationParams): Promise<ApiResponse<DailyReportSimple[]>> {
    try {
      if (!this.base) {
        return { data: [], success: false, error: 'Airtable not configured' };
      }

      const selectOptions: any = {
        maxRecords: params?.limit || 100,
        sort: [{ field: 'Report Date', direction: 'desc' }]
      };

      if (params?.search) {
        selectOptions.filterByFormula = `OR(SEARCH("${params.search}", {Candidate Name}), SEARCH("${params.search}", {Role Title}))`;
      }

      if (params?.status) {
        const statusFilter = `{Status} = "${params.status}"`;
        selectOptions.filterByFormula = selectOptions.filterByFormula
          ? `AND(${selectOptions.filterByFormula}, ${statusFilter})`
          : statusFilter;
      }

      if (params?.dateFrom && params?.dateTo) {
        const dateFilter = `AND(IS_AFTER({Report Date}, '${params.dateFrom.toISOString().split('T')[0]}'), IS_BEFORE({Report Date}, '${params.dateTo.toISOString().split('T')[0]}'))`;
        selectOptions.filterByFormula = selectOptions.filterByFormula
          ? `AND(${selectOptions.filterByFormula}, ${dateFilter})`
          : dateFilter;
      }

      const records = await this.base('Daily Reports - Simple').select(selectOptions).all();

      console.log('🔍 Raw Daily Reports Simple record debug:', {
        total: records.length,
        sampleRecord: records[0] ? {
          id: records[0].id,
          allFields: records[0].fields,
          currentStepField: records[0].get('Current Step'),
          currentStepFieldType: typeof records[0].get('Current Step')
        } : 'No records'
      });

      const reports: DailyReportSimple[] = records.map((record: any) =>
        this.transformRecord(record, {
          reportDate: new Date(record.get('Report Date')),
          roleTitle: record.get('Role Title')?.[0], // This is now a linked record to Positions table
          roleTitleLookup: record.get('Job Title (from Role Title)'), // Lookup field for job title
          candidateName: record.get('Candidate Name'), // This is now a linked record
          candidateLastName: record.get('Last Name'), // Lookup field for last name
          candidateId: record.get('Candidate Name')?.[0], // Link to candidate ID from linked record
          location: record.get('Location'), // This is now a lookup field
          practiceArea: record.get('Practice Area'),
          interviewSummary: record.get('Interview Summary'),
          nextActions: record.get('Next Actions'),
          status: record.get('Status'),
          createdBy: record.get('Created By'),
          currentStepFromTimeline: record.get('Current Step')
        })
      );

      return { data: reports, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  async createDailyReportSimple(report: Omit<DailyReportSimple, 'id' | 'createdDate'>): Promise<ApiResponse<DailyReportSimple>> {
    try {
      if (!this.base) {
        return { data: {} as DailyReportSimple, success: false, error: 'Airtable not configured' };
      }

      const record = await this.base('Daily Reports - Simple').create([
        {
          fields: {
            'Report Date': report.reportDate.toISOString().split('T')[0],
            'Role Title': report.roleTitle,
            'Candidate Name': report.candidateName,
            'Location': report.location,
            'Practice Area': report.practiceArea,
            'Interview Summary': report.interviewSummary,
            'Next Actions': report.nextActions,
            'Status': report.status,
            'Created By': report.createdBy
          }
        }
      ]);

      const createdReport = this.transformRecord(record[0], {
        reportDate: new Date(record[0].get('Report Date')),
        roleTitle: record[0].get('Role Title'),
        candidateName: record[0].get('Candidate Name'),
        location: record[0].get('Location'),
        practiceArea: record[0].get('Practice Area'),
        interviewSummary: record[0].get('Interview Summary'),
        nextActions: record[0].get('Next Actions'),
        status: record[0].get('Status'),
        createdBy: record[0].get('Created By')
      });

      return { data: createdReport, success: true };
    } catch (error) {
      return { data: {} as DailyReportSimple, success: false, error: (error as Error).message };
    }
  }

  // Daily Reports - Advanced
  async getDailyReportsAdvanced(params?: FilterParams & PaginationParams): Promise<ApiResponse<DailyReportAdvanced[]>> {
    try {
      if (!this.base) {
        return { data: [], success: false, error: 'Airtable not configured' };
      }

      const selectOptions: any = {
        maxRecords: params?.limit || 100,
        sort: [{ field: 'Report Date', direction: 'desc' }]
      };

      if (params?.search) {
        selectOptions.filterByFormula = `OR(SEARCH("${params.search}", {Candidate Name}), SEARCH("${params.search}", {Position Title}))`;
      }

      if (params?.status) {
        const statusFilter = `{Status} = "${params.status}"`;
        selectOptions.filterByFormula = selectOptions.filterByFormula
          ? `AND(${selectOptions.filterByFormula}, ${statusFilter})`
          : statusFilter;
      }

      if (params?.dateFrom && params?.dateTo) {
        const dateFilter = `AND(IS_AFTER({Report Date}, '${params.dateFrom.toISOString().split('T')[0]}'), IS_BEFORE({Report Date}, '${params.dateTo.toISOString().split('T')[0]}'))`;
        selectOptions.filterByFormula = selectOptions.filterByFormula
          ? `AND(${selectOptions.filterByFormula}, ${dateFilter})`
          : dateFilter;
      }

      const records = await this.base('Daily Reports - Advanced').select(selectOptions).all();

      console.log('🔍 Raw Daily Reports Advanced record debug:', {
        total: records.length,
        sampleRecord: records[0] ? {
          id: records[0].id,
          allFields: records[0].fields,
          currentStepField: records[0].get('Current Step'),
          currentStepFieldType: typeof records[0].get('Current Step')
        } : 'No records'
      });

      const reports: DailyReportAdvanced[] = records.map((record: any) =>
        this.transformRecord(record, {
          reportDate: new Date(record.get('Report Date')),
          candidateId: record.get('Candidate')?.[0],
          positionId: record.get('Position')?.[0],
          applicationId: record.get('Application')?.[0],
          interviewSummary: record.get('Interview Summary'),
          interviewFeedbackSummary: record.get('Interview Feedback Summary'),
          candidateRating: record.get('Candidate Rating'),
          nextSteps: record.get('Next Steps'),
          scheduledFollowup: record.get('Scheduled Follow-up') ? new Date(record.get('Scheduled Follow-up')) : undefined,
          priorityLevel: record.get('Priority Level'),
          status: record.get('Status'),
          salaryExpectation: record.get('Salary Expectation'),
          expectedStartDate: record.get('Expected Start Date') ? new Date(record.get('Expected Start Date')) : undefined,
          m4Consultant: record.get('M4 Consultant'),
          clientContact: record.get('Client Contact'),
          additionalNotes: record.get('Additional Notes'),
          currentStepFromTimeline: record.get('Current Step')
        })
      );

      return { data: reports, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  async createDailyReportAdvanced(report: Omit<DailyReportAdvanced, 'id' | 'createdDate'>): Promise<ApiResponse<DailyReportAdvanced>> {
    try {
      if (!this.base) {
        return { data: {} as DailyReportAdvanced, success: false, error: 'Airtable not configured' };
      }

      const fields: any = {
        'Report Date': report.reportDate.toISOString().split('T')[0],
        'Interview Summary': report.interviewSummary,
        'Next Steps': report.nextSteps,
        'Priority Level': report.priorityLevel,
        'Status': report.status,
        'M4 Consultant': report.m4Consultant
      };

      // Add optional fields if they exist
      if (report.candidateId) fields['Candidate'] = [report.candidateId];
      if (report.positionId) fields['Position'] = [report.positionId];
      if (report.applicationId) fields['Application'] = [report.applicationId];
      if (report.interviewFeedbackSummary) fields['Interview Feedback Summary'] = report.interviewFeedbackSummary;
      if (report.candidateRating) fields['Candidate Rating'] = report.candidateRating;
      if (report.scheduledFollowup) fields['Scheduled Follow-up'] = report.scheduledFollowup.toISOString().split('T')[0];
      if (report.salaryExpectation) fields['Salary Expectation'] = report.salaryExpectation;
      if (report.expectedStartDate) fields['Expected Start Date'] = report.expectedStartDate.toISOString().split('T')[0];
      if (report.clientContact) fields['Client Contact'] = report.clientContact;
      if (report.additionalNotes) fields['Additional Notes'] = report.additionalNotes;

      const record = await this.base('Daily Reports - Advanced').create([{ fields }]);

      const createdReport = this.transformRecord(record[0], {
        reportDate: new Date(record[0].get('Report Date')),
        candidateId: record[0].get('Candidate')?.[0],
        positionId: record[0].get('Position')?.[0],
        applicationId: record[0].get('Application')?.[0],
        interviewSummary: record[0].get('Interview Summary'),
        interviewFeedbackSummary: record[0].get('Interview Feedback Summary'),
        candidateRating: record[0].get('Candidate Rating'),
        nextSteps: record[0].get('Next Steps'),
        scheduledFollowup: record[0].get('Scheduled Follow-up') ? new Date(record[0].get('Scheduled Follow-up')) : undefined,
        priorityLevel: record[0].get('Priority Level'),
        status: record[0].get('Status'),
        salaryExpectation: record[0].get('Salary Expectation'),
        expectedStartDate: record[0].get('Expected Start Date') ? new Date(record[0].get('Expected Start Date')) : undefined,
        m4Consultant: record[0].get('M4 Consultant'),
        clientContact: record[0].get('Client Contact'),
        additionalNotes: record[0].get('Additional Notes')
      });

      return { data: createdReport, success: true };
    } catch (error) {
      return { data: {} as DailyReportAdvanced, success: false, error: (error as Error).message };
    }
  }

  // Audit Logging Methods
  async createAuditLog(auditData: Omit<AuditLog, 'id' | 'timestamp'>): Promise<ApiResponse<AuditLog>> {
    try {
      if (!this.base) {
        return { data: {} as AuditLog, success: false, error: 'Airtable not configured' };
      }

      const fields: any = {
        'Timestamp': new Date().toISOString(),
        'Action': auditData.action,
        'Description': auditData.description,
        'Result': auditData.result
      };

      if (auditData.userId) fields['User'] = [auditData.userId];
      if (auditData.ipAddress) fields['IP Address'] = auditData.ipAddress;
      if (auditData.userAgent) fields['User Agent'] = auditData.userAgent;

      const record = await this.base('Audit Log').create([{ fields }]);

      const createdAuditLog = this.transformRecord(record[0], {
        timestamp: new Date(record[0].get('Timestamp')),
        userId: record[0].get('User')?.[0],
        action: record[0].get('Action'),
        description: record[0].get('Description'),
        ipAddress: record[0].get('IP Address'),
        userAgent: record[0].get('User Agent'),
        result: record[0].get('Result')
      });

      return { data: createdAuditLog, success: true };
    } catch (error) {
      return { data: {} as AuditLog, success: false, error: (error as Error).message };
    }
  }

  async getAuditLogs(params?: FilterParams & PaginationParams): Promise<ApiResponse<AuditLog[]>> {
    try {
      if (!this.base) {
        return { data: [], success: false, error: 'Airtable not configured' };
      }

      const selectOptions: any = {
        maxRecords: params?.limit || 100,
        sort: [{ field: 'Timestamp', direction: 'desc' }]
      };

      if (params?.search) {
        selectOptions.filterByFormula = `OR(SEARCH("${params.search}", {Action}), SEARCH("${params.search}", {Description}))`;
      }

      const records = await this.base('Audit Log').select(selectOptions).all();

      const auditLogs: AuditLog[] = records.map((record: any) =>
        this.transformRecord(record, {
          timestamp: new Date(record.get('Timestamp')),
          userId: record.get('User')?.[0],
          action: record.get('Action'),
          description: record.get('Description'),
          ipAddress: record.get('IP Address'),
          userAgent: record.get('User Agent'),
          result: record.get('Result')
        })
      );

      return { data: auditLogs, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  // Session Management Methods
  async createUserSession(sessionData: Omit<UserSession, 'id'>): Promise<ApiResponse<UserSession>> {
    try {
      if (!this.base) {
        return { data: {} as UserSession, success: false, error: 'Airtable not configured' };
      }

      const fields: any = {
        'Session ID': sessionData.sessionId,
        'User': [sessionData.userId],
        'Login Time': sessionData.loginTime.toISOString(),
        'Last Activity': sessionData.lastActivity.toISOString(),
        'Expires': sessionData.expires.toISOString(),
        'Active': sessionData.active
      };

      if (sessionData.ipAddress) fields['IP Address'] = sessionData.ipAddress;
      if (sessionData.userAgent) fields['User Agent'] = sessionData.userAgent;

      const record = await this.base('User Sessions').create([{ fields }]);

      const createdSession = this.transformRecord(record[0], {
        sessionId: record[0].get('Session ID'),
        userId: record[0].get('User')?.[0],
        ipAddress: record[0].get('IP Address'),
        userAgent: record[0].get('User Agent'),
        loginTime: new Date(record[0].get('Login Time')),
        lastActivity: new Date(record[0].get('Last Activity')),
        expires: new Date(record[0].get('Expires')),
        active: record[0].get('Active')
      });

      return { data: createdSession, success: true };
    } catch (error) {
      return { data: {} as UserSession, success: false, error: (error as Error).message };
    }
  }

  async updateUserSession(sessionId: string, updates: Partial<UserSession>): Promise<ApiResponse<UserSession>> {
    try {
      if (!this.base) {
        return { data: {} as UserSession, success: false, error: 'Airtable not configured' };
      }

      const fields: any = {};
      if (updates.lastActivity) fields['Last Activity'] = updates.lastActivity.toISOString();
      if (updates.expires) fields['Expires'] = updates.expires.toISOString();
      if (typeof updates.active === 'boolean') fields['Active'] = updates.active;

      const record = await this.base('User Sessions').update([
        {
          id: sessionId,
          fields: fields
        }
      ]);

      const updatedSession = this.transformRecord(record[0], {
        sessionId: record[0].get('Session ID'),
        userId: record[0].get('User')?.[0],
        ipAddress: record[0].get('IP Address'),
        userAgent: record[0].get('User Agent'),
        loginTime: new Date(record[0].get('Login Time')),
        lastActivity: new Date(record[0].get('Last Activity')),
        expires: new Date(record[0].get('Expires')),
        active: record[0].get('Active')
      });

      return { data: updatedSession, success: true };
    } catch (error) {
      return { data: {} as UserSession, success: false, error: (error as Error).message };
    }
  }

  async getUserSessions(userId?: string): Promise<ApiResponse<UserSession[]>> {
    try {
      if (!this.base) {
        return { data: [], success: false, error: 'Airtable not configured' };
      }

      const selectOptions: any = {
        maxRecords: 100,
        sort: [{ field: 'Login Time', direction: 'desc' }]
      };

      if (userId) {
        selectOptions.filterByFormula = `{User} = "${userId}"`;
      }

      const records = await this.base('User Sessions').select(selectOptions).all();

      const sessions: UserSession[] = records.map((record: any) =>
        this.transformRecord(record, {
          sessionId: record.get('Session ID'),
          userId: record.get('User')?.[0],
          ipAddress: record.get('IP Address'),
          userAgent: record.get('User Agent'),
          loginTime: new Date(record.get('Login Time')),
          lastActivity: new Date(record.get('Last Activity')),
          expires: new Date(record.get('Expires')),
          active: record.get('Active')
        })
      );

      return { data: sessions, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  // Enhanced User Management Methods
  async updateUserLoginAttempts(userId: string, failed: boolean = false): Promise<ApiResponse<void>> {
    try {
      if (!this.base) {
        return { data: undefined, success: false, error: 'Airtable not configured' };
      }

      const user = await this.base('Users').find(userId);
      const currentFailedAttempts = user.get('Failed Login Attempts') || 0;

      const fields: any = {};

      if (failed) {
        const newFailedAttempts = currentFailedAttempts + 1;
        fields['Failed Login Attempts'] = newFailedAttempts;

        // Lock account after 5 failed attempts
        if (newFailedAttempts >= 5) {
          fields['Account Locked'] = true;
          const lockExpires = new Date();
          lockExpires.setMinutes(lockExpires.getMinutes() + 30); // Lock for 30 minutes
          fields['Lock Expires'] = lockExpires.toISOString();
        }
      } else {
        // Reset failed attempts on successful login
        fields['Failed Login Attempts'] = 0;
        fields['Account Locked'] = false;
        fields['Lock Expires'] = null;
      }

      await this.base('Users').update([
        {
          id: userId,
          fields: fields
        }
      ]);

      return { data: undefined, success: true };
    } catch (error) {
      return { data: undefined, success: false, error: (error as Error).message };
    }
  }

  // Email Templates
  async getEmailTemplates(category?: string): Promise<ApiResponse<EmailTemplate[]>> {
    try {
      if (!this.base) {
        return { data: [], success: false, error: 'Airtable not configured' };
      }

      const selectOptions: any = {
        maxRecords: 100,
        sort: [{ field: 'Template Name', direction: 'asc' }]
      };

      if (category) {
        selectOptions.filterByFormula = `{Template Category} = "${category}"`;
      }

      const records = await this.base('Email Templates').select(selectOptions).all();

      const templates: EmailTemplate[] = records.map((record: any) =>
        this.transformRecord(record, {
          templateName: record.get('Template Name'),
          subjectLine: record.get('Subject Line'),
          emailBody: record.get('Email Body'),
          templateCategory: record.get('Template Category'),
          variablesUsed: record.get('Variables Used') || [],
          active: record.get('Active') !== false,
          createdBy: record.get('Created By'),
          lastModified: new Date(record.get('Last Modified') || record.createdTime)
        })
      );

      return { data: templates, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  async createEmailTemplate(template: Omit<EmailTemplate, 'id' | 'createdDate'>): Promise<ApiResponse<EmailTemplate>> {
    try {
      if (!this.base) {
        return { data: {} as EmailTemplate, success: false, error: 'Airtable not configured' };
      }

      const record = await this.base('Email Templates').create([
        {
          fields: {
            'Template Name': template.templateName,
            'Subject Line': template.subjectLine,
            'Email Body': template.emailBody,
            'Template Category': template.templateCategory,
            'Variables Used': template.variablesUsed,
            'Active': template.active,
            'Created By': template.createdBy,
            'Last Modified': new Date().toISOString()
          }
        }
      ]);

      const createdTemplate = this.transformRecord(record[0], {
        templateName: record[0].get('Template Name'),
        subjectLine: record[0].get('Subject Line'),
        emailBody: record[0].get('Email Body'),
        templateCategory: record[0].get('Template Category'),
        variablesUsed: record[0].get('Variables Used'),
        active: record[0].get('Active'),
        createdBy: record[0].get('Created By'),
        lastModified: new Date(record[0].get('Last Modified'))
      });

      return { data: createdTemplate, success: true };
    } catch (error) {
      return { data: {} as EmailTemplate, success: false, error: (error as Error).message };
    }
  }

  async updateEmailTemplate(templateId: string, updates: Partial<EmailTemplate>): Promise<ApiResponse<EmailTemplate>> {
    try {
      if (!this.base) {
        return { data: {} as EmailTemplate, success: false, error: 'Airtable not configured' };
      }

      const fields: any = { 'Last Modified': new Date().toISOString() };

      if (updates.templateName) fields['Template Name'] = updates.templateName;
      if (updates.subjectLine) fields['Subject Line'] = updates.subjectLine;
      if (updates.emailBody) fields['Email Body'] = updates.emailBody;
      if (updates.templateCategory) fields['Template Category'] = updates.templateCategory;
      if (updates.variablesUsed) fields['Variables Used'] = updates.variablesUsed;
      if (updates.active !== undefined) fields['Active'] = updates.active;

      const record = await this.base('Email Templates').update([
        {
          id: templateId,
          fields: fields
        }
      ]);

      const updatedTemplate = this.transformRecord(record[0], {
        templateName: record[0].get('Template Name'),
        subjectLine: record[0].get('Subject Line'),
        emailBody: record[0].get('Email Body'),
        templateCategory: record[0].get('Template Category'),
        variablesUsed: record[0].get('Variables Used'),
        active: record[0].get('Active'),
        createdBy: record[0].get('Created By'),
        lastModified: new Date(record[0].get('Last Modified'))
      });

      return { data: updatedTemplate, success: true };
    } catch (error) {
      return { data: {} as EmailTemplate, success: false, error: (error as Error).message };
    }
  }

  // Email Conversations
  async getEmailConversations(candidateId?: string): Promise<ApiResponse<EmailConversation[]>> {
    try {
      if (!this.base) {
        return { data: [], success: false, error: 'Airtable not configured' };
      }

      const selectOptions: any = {
        maxRecords: 100,
        sort: [{ field: 'Last Activity Date', direction: 'desc' }]
      };

      if (candidateId) {
        selectOptions.filterByFormula = `{Candidate} = "${candidateId}"`;
      }

      const records = await this.base('Email Conversations').select(selectOptions).all();

      const conversations: EmailConversation[] = records.map((record: any) =>
        this.transformRecord(record, {
          candidateId: record.get('Candidate')?.[0],
          applicationId: record.get('Application')?.[0],
          subject: record.get('Subject'),
          conversationThread: [], // Will be loaded separately
          status: record.get('Status'),
          lastActivityDate: new Date(record.get('Last Activity Date')),
          priority: record.get('Priority'),
          assignedTo: record.get('Assigned To'),
          tags: record.get('Tags') || []
        })
      );

      return { data: conversations, success: true };
    } catch (error) {
      return { data: [], success: false, error: (error as Error).message };
    }
  }

  async createEmailConversation(conversation: Omit<EmailConversation, 'id' | 'createdDate'>): Promise<ApiResponse<EmailConversation>> {
    try {
      if (!this.base) {
        return { data: {} as EmailConversation, success: false, error: 'Airtable not configured' };
      }

      const fields: any = {
        'Candidate': [conversation.candidateId],
        'Subject': conversation.subject,
        'Status': conversation.status,
        'Last Activity Date': conversation.lastActivityDate.toISOString(),
        'Priority': conversation.priority
      };

      if (conversation.applicationId) fields['Application'] = [conversation.applicationId];
      if (conversation.assignedTo) fields['Assigned To'] = conversation.assignedTo;
      if (conversation.tags) fields['Tags'] = conversation.tags;

      const record = await this.base('Email Conversations').create([{ fields }]);

      const createdConversation = this.transformRecord(record[0], {
        candidateId: record[0].get('Candidate')?.[0],
        applicationId: record[0].get('Application')?.[0],
        subject: record[0].get('Subject'),
        conversationThread: [],
        status: record[0].get('Status'),
        lastActivityDate: new Date(record[0].get('Last Activity Date')),
        priority: record[0].get('Priority'),
        assignedTo: record[0].get('Assigned To'),
        tags: record[0].get('Tags') || []
      });

      return { data: createdConversation, success: true };
    } catch (error) {
      return { data: {} as EmailConversation, success: false, error: (error as Error).message };
    }
  }

  async createConversationMessage(message: Omit<ConversationMessage, 'id'>): Promise<ApiResponse<ConversationMessage>> {
    try {
      if (!this.base) {
        return { data: {} as ConversationMessage, success: false, error: 'Airtable not configured' };
      }

      const fields: any = {
        'Conversation': [message.conversationId],
        'Message Type': message.messageType,
        'Message Body': message.messageBody,
        'Is Read': message.isRead
      };

      if (message.subject) fields['Subject'] = message.subject;
      if (message.htmlBody) fields['HTML Body'] = message.htmlBody;
      if (message.sentBy) fields['Sent By'] = message.sentBy;
      if (message.sentDate) fields['Sent Date'] = message.sentDate.toISOString();
      if (message.receivedDate) fields['Received Date'] = message.receivedDate.toISOString();
      if (message.messageId) fields['Message ID'] = message.messageId;
      if (message.templateUsed) fields['Template Used'] = [message.templateUsed];
      if (message.deliveryStatus) fields['Delivery Status'] = message.deliveryStatus;
      if (message.readDate) fields['Read Date'] = message.readDate.toISOString();

      const record = await this.base('Conversation Messages').create([{ fields }]);

      const createdMessage = this.transformRecord(record[0], {
        conversationId: record[0].get('Conversation')?.[0],
        messageType: record[0].get('Message Type'),
        subject: record[0].get('Subject'),
        messageBody: record[0].get('Message Body'),
        htmlBody: record[0].get('HTML Body'),
        sentBy: record[0].get('Sent By'),
        sentDate: record[0].get('Sent Date') ? new Date(record[0].get('Sent Date')) : undefined,
        receivedDate: record[0].get('Received Date') ? new Date(record[0].get('Received Date')) : undefined,
        messageId: record[0].get('Message ID'),
        templateUsed: record[0].get('Template Used')?.[0],
        deliveryStatus: record[0].get('Delivery Status'),
        attachments: [],
        isRead: record[0].get('Is Read'),
        readDate: record[0].get('Read Date') ? new Date(record[0].get('Read Date')) : undefined
      });

      return { data: createdMessage, success: true };
    } catch (error) {
      return { data: {} as ConversationMessage, success: false, error: (error as Error).message };
    }
  }
}

export const airtableService = new AirtableService();
export default airtableService;